/**
 * Business events for Grafana Cloud Frontend Observability (Faro).
 * Complements GTM / Loki / Netcore — does not replace them.
 *
 * All attribute values must be strings (Faro API requirement).
 * Sensitive fields are stripped before export.
 */

import { isGrafanaFaroEnabled } from '@/lib/grafana-faro-enabled';

const SENSITIVE_KEY =
  /password|passwd|secret|token|authorization|cookie|cvv|cvc|card_number|cardnumber|credit_card|creditcard|pan\b|cc_number|refresh_token|access_token|id_token/i;

/** Stable event names for dashboards and external analytics. */
export const FaroEventName = {
  REGISTER: 'register',
  LOGIN: 'login',
  LOGIN_FAILED: 'login_failed',
  ADD_TO_CART: 'add_to_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  CHECKOUT_INIT: 'checkout_init',
  PLACE_ORDER: 'place_order',
} as const;

export type FaroEventNameType =
  (typeof FaroEventName)[keyof typeof FaroEventName];

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY.test(key);
}

/**
 * Flattens nested objects into string attributes safe for Faro.
 * Ecommerce `items` arrays are summarized (count + ids) to limit cardinality.
 */
export function toFaroAttributes(
  data: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};

  const walk = (obj: Record<string, unknown>, prefix: string) => {
    for (const [key, value] of Object.entries(obj)) {
      if (isSensitiveKey(key)) continue;

      const attrKey = prefix ? `${prefix}_${key}` : key;

      if (value === null || value === undefined) continue;

      if (key === 'items' && Array.isArray(value)) {
        out[`${attrKey}_count`] = String(value.length);
        const ids = value
          .slice(0, 25)
          .map((item) => {
            if (item && typeof item === 'object') {
              const row = item as Record<string, unknown>;
              return String(row.item_id ?? row.item_sku ?? row.variant_id ?? '');
            }
            return '';
          })
          .filter(Boolean);
        if (ids.length > 0) {
          out[`${attrKey}_ids`] = ids.join(',');
        }
        continue;
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        walk(value as Record<string, unknown>, attrKey);
        continue;
      }

      if (Array.isArray(value)) {
        out[attrKey] = JSON.stringify(value).slice(0, 400);
        continue;
      }

      out[attrKey] = String(value);
    }
  };

  walk(data, '');
  return out;
}

/**
 * Push a custom business event to Grafana Cloud. No-op when Faro is disabled.
 */
export function faroTrackEvent(
  name: FaroEventNameType | string,
  attributes?: Record<string, string>,
  domain = 'qualidoc',
): void {
  if (!isGrafanaFaroEnabled) return;

  void import('@/lib/grafana-faro').then(async ({ grafanaFaroInit, getGrafanaFaro }) => {
    await grafanaFaroInit();
    const faro = getGrafanaFaro();
    if (!faro) return;

    try {
      faro.api.pushEvent(name, attributes ?? {}, domain);
    } catch (err) {
      console.warn('[Faro] Failed to push event:', name, err);
    }
  });
}

/** Track from a plain object (e.g. GTM ecommerce payload). */
export function faroTrackFromPayload(
  name: FaroEventNameType | string,
  payload: Record<string, unknown>,
  domain = 'qualidoc',
): void {
  faroTrackEvent(name, toFaroAttributes(payload), domain);
}

export type FaroLoginFailedParams = {
  method: 'email_password' | 'social' | 'google_mobile' | 'apple_mobile' | 'otp';
  reason?: string;
};

export function faroTrackLoginFailed(params: FaroLoginFailedParams): void {
  faroTrackEvent(
    FaroEventName.LOGIN_FAILED,
    {
      method: params.method,
      ...(params.reason ? { reason: params.reason.slice(0, 200) } : {}),
    },
    'auth',
  );
}
